import { useEffect, useMemo, useRef, useState } from 'react';
import {
    ArrowClockwise,
    CaretDown,
    Check,
    FileExport,
    MagnifyingGlass,
    PencilSimple,
    Plus,
    ShoppingCart,
    Trash,
    Warehouse,
} from 'shared/icons';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import { GetPeriodLabel, GetPeriodRangeText, GetPeriodSuffix, PERIOD_GROUPS } from '../periodHelpers';
import { Backdrop } from './Backdrop';
import { BrandTagPicker } from './BrandTagPicker';
import { DateRangePicker } from './DateRangePicker';
import { KbdHint } from './KbdHint';
import { LayoutToggle } from './LayoutToggle';
import { TBtn, TChip, TDItem, TDivider, TInput } from './primitives';
import { FormatStockRuleLabel, HasStockRuleFor, MakeDefaultStockRule, StockRuleEditor } from './StockRuleEditor';
import { useTokens } from './tokens';

/**
 * "Visão Diária · 30d" — junta nome do preset com sufixo do período padrão dele.
 * Tolerante a `p = null` (boot inicial / lista vazia) — devolve string vazia
 * pra que o caller use placeholder próprio.
 */
function formatPresetName(p) {
    if (!p?.name) return '';
    const suffix = GetPeriodSuffix(p?.filters?.period);
    return suffix ? `${p.name} · ${suffix}` : p.name;
}

function PresetSelector({
    preset,
    presets,
    onPresetChange,
    onEditPreset,
    onNewPreset,
    onDeletePreset,
    editing,
    currentUserId,
}) {
    const T = useTokens();
    const [open, setOpen] = useState(false);
    // Preset aguardando confirmação de exclusão (ConfirmModal, não window.confirm).
    const [deleteTarget, setDeleteTarget] = useState(null);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        const h = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [open]);

    const byCategory = presets.reduce((acc, p) => {
        if (!acc[p.category]) {
            acc[p.category] = [];
        }
        acc[p.category].push(p);
        return acc;
    }, {});

    // Em modo de edição, o selector vira label estático "● editando: <nome>".
    if (editing) {
        return (
            <div
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    border: `2px solid ${T.ink}`,
                    padding: '4px 10px',
                    borderRadius: 4,
                }}
            >
                <span
                    style={{
                        fontSize: 10,
                        color: T.accent,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                    }}
                >
                    ● editando
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
                    {editing?.name?.trim() || 'Novo preset'}
                </span>
            </div>
        );
    }

    // Sem preset ativo (boot inicial ou lista vazia): label inerte que abre o
    // dropdown só pra criar. Evita crash em formatPresetName(null).
    if (!preset) {
        return (
            <div ref={ref} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <Backdrop open={open && presets.length > 0} onClose={() => setOpen(false)} />
                <button
                    type="button"
                    onClick={() => {
                        if (presets.length === 0) {
                            onNewPreset?.();
                        } else {
                            setOpen((o) => !o);
                        }
                    }}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 7,
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: 4,
                        background: 'transparent',
                        outline: 'none',
                    }}
                >
                    <span style={{ fontSize: 13, fontStyle: 'italic', color: T.muted, fontFamily: T.sans }}>
                        {presets.length === 0 ? '+ criar primeiro preset' : 'Selecionar preset…'}
                    </span>
                    {presets.length > 0 && <CaretDown size={14} color={T.muted} />}
                </button>
                {open && presets.length > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 'calc(100% + 8px)',
                            left: 0,
                            zIndex: 300,
                            background: T.bg,
                            border: `1px solid ${T.line}`,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            borderRadius: 6,
                            width: 300,
                            overflow: 'hidden',
                        }}
                    >
                        {Object.entries(byCategory).map(([cat, ps]) => (
                            <div key={cat}>
                                <div
                                    style={{
                                        padding: '8px 14px 4px',
                                        fontSize: 10,
                                        fontWeight: 600,
                                        color: T.muted,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                    }}
                                >
                                    {cat}
                                </div>
                                {ps.map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => {
                                            onPresetChange(p);
                                            setOpen(false);
                                        }}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            padding: '8px 14px',
                                            cursor: 'pointer',
                                            background: 'transparent',
                                            border: 'none',
                                            textAlign: 'left',
                                            outline: 'none',
                                            fontSize: 13,
                                            color: T.ink,
                                        }}
                                    >
                                        {formatPresetName(p)}
                                    </button>
                                ))}
                            </div>
                        ))}
                        <div style={{ borderTop: `1px solid ${T.line}`, padding: '6px 8px' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    onNewPreset?.();
                                }}
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: 12,
                                    color: T.accent,
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    borderRadius: 4,
                                    outline: 'none',
                                }}
                            >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <Plus size={12} /> Novo preset
                                </span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            ref={ref}
            style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
            data-faid="Preset_Selector_Toolbar_Operacoes"
        >
            <Backdrop open={open} onClose={() => setOpen(false)} />
            <button
                type="button"
                data-faid="Preset_Selector_Trigger_Operacoes"
                onClick={() => setOpen((o) => !o)}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: 4,
                    background: open ? T.bgMid : 'transparent',
                    outline: 'none',
                }}
            >
                <span style={{ fontSize: 14, fontWeight: 600, color: T.ink, fontFamily: T.sans }}>
                    {formatPresetName(preset)}
                </span>
                <CaretDown size={14} color={T.muted} />
            </button>

            {open && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        left: 0,
                        zIndex: 300,
                        background: T.bg,
                        border: `1px solid ${T.line}`,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        borderRadius: 6,
                        width: 300,
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            padding: '8px 14px 6px',
                            fontSize: 10,
                            color: T.muted,
                            textTransform: 'uppercase',
                            letterSpacing: 0.6,
                            borderBottom: `1px solid ${T.line}`,
                        }}
                    >
                        Presets
                    </div>
                    {Object.entries(byCategory).map(([cat, ps]) => (
                        <div key={cat}>
                            <div
                                style={{
                                    padding: '8px 14px 4px',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: T.muted,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                }}
                            >
                                {cat}
                            </div>
                            {ps.map((p) => {
                                const active = p.id === preset?.id;
                                // Pessoal só dono pode editar/excluir; demais qualquer um da org.
                                const canMutate =
                                    p.category !== 'Pessoal' || (currentUserId && p.createdBy === currentUserId);
                                return (
                                    <div
                                        key={p.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            padding: '4px 8px 4px 14px',
                                            background: active ? T.bgMid : 'transparent',
                                            borderLeft: active ? `2px solid ${T.ink}` : '2px solid transparent',
                                        }}
                                    >
                                        <button
                                            type="button"
                                            data-faid="Preset_Item_Operacoes"
                                            data-preset-name={p.name}
                                            onClick={() => {
                                                onPresetChange(p);
                                                setOpen(false);
                                            }}
                                            style={{
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                padding: '4px 6px',
                                                cursor: 'pointer',
                                                background: 'transparent',
                                                border: 'none',
                                                textAlign: 'left',
                                                outline: 'none',
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 13,
                                                    fontWeight: active ? 600 : 400,
                                                    color: T.ink,
                                                    flex: 1,
                                                }}
                                            >
                                                {formatPresetName(p)}
                                            </span>
                                            {active && <Check size={13} color={T.ink} />}
                                        </button>
                                        {canMutate && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpen(false);
                                                        onEditPreset?.(p);
                                                    }}
                                                    title="Editar preset"
                                                    style={{
                                                        background: 'transparent',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        color: T.muted,
                                                        padding: 4,
                                                        display: 'inline-flex',
                                                        outline: 'none',
                                                    }}
                                                >
                                                    <PencilSimple size={12} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpen(false);
                                                        setDeleteTarget(p);
                                                    }}
                                                    title="Excluir preset"
                                                    style={{
                                                        background: 'transparent',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        color: T.muted,
                                                        padding: 4,
                                                        display: 'inline-flex',
                                                        outline: 'none',
                                                    }}
                                                >
                                                    <Trash size={12} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                    <div style={{ borderTop: `1px solid ${T.line}`, padding: '6px 8px' }}>
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                                onNewPreset?.();
                            }}
                            style={{
                                width: '100%',
                                padding: '6px 10px',
                                fontSize: 12,
                                color: T.accent,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                textAlign: 'left',
                                borderRadius: 4,
                                outline: 'none',
                            }}
                        >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Plus size={12} /> Novo preset
                            </span>
                        </button>
                    </div>
                </div>
            )}

            {/* Confirmação de exclusão de preset — ConfirmModal do design system
                (variante danger) no lugar do antigo window.confirm nativo. */}
            <ConfirmModal
                isOpen={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => {
                    onDeletePreset?.(deleteTarget);
                    setDeleteTarget(null);
                }}
                title="Excluir preset"
                message={`Excluir o preset "${deleteTarget?.name}"?`}
                confirmText="Excluir"
                confirmVariant="danger"
            />
        </div>
    );
}

function CartButton({ count, onClick }) {
    const T = useTokens();
    return (
        <button
            type="button"
            onClick={onClick}
            title="Carrinho de compra"
            data-faid="Cart_Button_Toolbar_Operacoes"
            style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 32,
                padding: '0 12px',
                background: count > 0 ? T.ink : T.bg,
                color: count > 0 ? T.bg : T.ink,
                border: `1px solid ${count > 0 ? T.ink : T.line}`,
                borderRadius: 4,
                cursor: 'pointer',
                fontFamily: T.sans,
                fontSize: 13,
                outline: 'none',
            }}
        >
            <ShoppingCart size={16} />
            <span>Carrinho</span>
            {count > 0 && (
                <span
                    style={{
                        display: 'inline-grid',
                        placeItems: 'center',
                        minWidth: 18,
                        height: 18,
                        padding: '0 5px',
                        marginLeft: 2,
                        borderRadius: 99,
                        background: T.warn,
                        color: '#fff',
                        fontFamily: T.mono,
                        fontWeight: 700,
                        fontSize: 11,
                        lineHeight: 1,
                    }}
                >
                    {count > 99 ? '99+' : count}
                </span>
            )}
        </button>
    );
}

function presetIncludesBlingColumn(preset) {
    const cols = Array.isArray(preset?.columns) ? preset.columns : [];
    return cols.some((c) => {
        const id = typeof c === 'string' ? c : c?.id;
        return id === 'bling' || id === 'stockTotal';
    });
}

/**
 * Toolbar unificada — substitui HeaderBar+FilterBar. Layout em linha única:
 *   [busca] | [preset selector] [chips de filtro] [+filtro]   ...   [Carrinho · Depósitos · Exportar · Exibição]
 *
 * Estatísticas (X pedidos · Y itens) NÃO ficam aqui — elas migraram pro footer da tabela.
 */
export function Toolbar({
    preset,
    presets,
    onPresetChange,
    onEditPreset,
    onNewPreset,
    onDeletePreset,
    editing,
    currentUserId,
    layoutMode,
    onLayoutChange,
    overrides,
    onOverride,
    onRemoveOverride,
    brandOpts,
    tagOpts = [],
    onExport,
    search,
    onSearchChange,
    onOpenWarehouseConfig,
    cartCount,
    onOpenCart,
    showCart,
    onReload,
    reloading,
    warehouses = [],
    // Range UTC efetivo do período ativo — usado pra pré-preencher o calendário
    // de datas específicas (em vez de abrir vazio) e pra mostrar dd/mm até
    // dd/mm no chip quando period='custom'.
    periodRange = null,
    // Range ISO UTC de CADA coluna do preset ({col: {from, to}}) — exposto no
    // DOM (data-col-ranges) pro harness E2E validar cada coluna no seu range.
    colRanges = null,
}) {
    const T = useTokens();
    const [openChip, setOpenChip] = useState(null);
    // `dateRangeOpen` é INDEPENDENTE do openChip — clique em "Datas específicas..."
    // fecha o period dropdown e abre o picker num popover dedicado (maior).
    // Antes o calendário aparecia squished dentro do dropdown de período.
    const [dateRangeOpen, setDateRangeOpen] = useState(false);
    const ref = useRef(null);

    // Fecha o popover de datas se clicar fora do toolbar inteiro (mesma lógica
    // do openChip — `ref` é o container da Toolbar).
    useEffect(() => {
        if (!dateRangeOpen) return undefined;
        const h = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setDateRangeOpen(false);
            }
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [dateRangeOpen]);

    useEffect(() => {
        if (!openChip) {
            return;
        }
        const h = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpenChip(null);
            }
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [openChip]);

    const [addOpen, setAddOpen] = useState(false);
    const addRef = useRef(null);

    useEffect(() => {
        if (!addOpen) {
            return;
        }
        const h = (e) => {
            if (addRef.current && !addRef.current.contains(e.target)) {
                setAddOpen(false);
            }
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [addOpen]);

    // Tolerante a `preset = null` (boot inicial enquanto carrega do backend).
    const eff = { ...(preset?.filters || {}), ...overrides };

    // `brands` e `tags` agora são arrays (multi-select). Mantemos `brand`/`tag`
    // single-string só pra retrocompat de presets antigos — quando vier valor
    // single + array vazio, transformamos em array de 1 pra UI ficar consistente.
    const effBrands = useMemo(() => {
        if (Array.isArray(eff.brands)) return eff.brands;
        if (eff.brand) return [eff.brand];
        return [];
    }, [eff.brands, eff.brand]);
    const effTags = useMemo(() => {
        if (Array.isArray(eff.tags)) return eff.tags;
        if (eff.tag) return [eff.tag];
        return [];
    }, [eff.tags, eff.tag]);

    const chips = useMemo(() => {
        const list = [];
        if (eff.period) {
            const rangeText = GetPeriodRangeText(eff.period);
            // Pra `custom` (datas específicas): chip mostra o RANGE escolhido
            // ("19/05/2026 até 21/05/2026") em vez do label genérico "Datas
            // específicas...". Fica explícito qual intervalo está aplicado.
            let label = GetPeriodLabel(eff.period);
            if (eff.period === 'custom' && eff.dateFrom && eff.dateTo) {
                // ISO YYYY-MM-DD → DD/MM/YYYY (string slicing, sem TZ leak).
                const fp = String(eff.dateFrom).split('-');
                const tp = String(eff.dateTo).split('-');
                const fLabel = fp.length === 3 ? `${fp[2]}/${fp[1]}/${fp[0]}` : eff.dateFrom;
                const tLabel = tp.length === 3 ? `${tp[2]}/${tp[1]}/${tp[0]}` : eff.dateTo;
                label = `${fLabel} até ${tLabel}`;
            }
            list.push({
                id: 'period',
                label,
                title: rangeText || label,
                removable: true,
                dropdown: true,
            });
        }
        // Renderiza chip se a chave do override existe (mesmo array vazio). Sem
        // isso, o "+ filtro → Marca" abria um picker SEPARADO sob o +filtro
        // (pendingFilter) e ao primeiro pick aparecia outro chip — dois pickers
        // visíveis simultaneamente, com o mesmo state. Agora há um único lugar
        // pro picker (o chip), e o "+ filtro" só inicializa o override + abre
        // esse chip.
        if (Array.isArray(eff.brands) || eff.brand) {
            const cnt = effBrands.length;
            list.push({
                id: 'brand',
                label: cnt === 0 ? '— selecione marcas —' : cnt === 1 ? `marca: ${effBrands[0]}` : `marcas: ${cnt}`,
                title: cnt > 1 ? effBrands.join(', ') : undefined,
                removable: true,
                dropdown: true,
                empty: cnt === 0,
            });
        }
        if (Array.isArray(eff.tags) || eff.tag) {
            const cnt = effTags.length;
            list.push({
                id: 'tag',
                label: cnt === 0 ? '— selecione tags —' : cnt === 1 ? `tag: ${effTags[0]}` : `tags: ${cnt}`,
                title: cnt > 1 ? effTags.join(', ') : undefined,
                removable: true,
                dropdown: true,
                empty: cnt === 0,
            });
        }
        // Stock rules — cada regra do array vira um chip independente.
        // ID composto `stock_rule:idx` identifica qual regra abrir/remover.
        const stockRulesList = Array.isArray(eff.stock_rules) ? eff.stock_rules : [];
        stockRulesList.forEach((rule, idx) => {
            list.push({
                id: `stock_rule:${idx}`,
                label: FormatStockRuleLabel(rule, warehouses),
                title: FormatStockRuleLabel(rule, warehouses),
                removable: true,
                dropdown: true,
                empty: !rule?.value,
                kind: 'stock_rule',
                ruleIndex: idx,
            });
        });
        return list;
    }, [
        eff.period,
        eff.dateFrom,
        eff.dateTo,
        eff.brand,
        eff.brands,
        eff.tag,
        eff.tags,
        eff.stock_rules,
        effBrands,
        effTags,
        warehouses,
    ]);

    /**
     * Cleanup de chip vazio quando o picker fecha. Detecta a transição
     * `openChip: 'brand' → null` (ou similar) via ref, e descarta o override
     * se não há valor real. Mais robusto que tentar interceptar TODOS caminhos
     * de fechamento (click outside, X do chip, toggle do próprio chip, etc).
     *
     * Usa snapshot de eff no momento que openChip muda — `eff.brands` é re-
     * computado em todo render, então o snapshot fica sempre fresh.
     */
    const prevOpenChipRef = useRef(null);
    useEffect(() => {
        const prevId = prevOpenChipRef.current;
        prevOpenChipRef.current = openChip;
        // Só processa cleanup quando FECHOU um picker (prev existia, atual null).
        if (!prevId || openChip === prevId) return;
        // Bug: cenário "preset tem 3 tags + user clica + filtro → tag → fecha":
        //   - "+ filtro tag" faz onOverride('tags', []) → mascara preset
        //   - Cleanup vê []. Se chamasse onRemoveOverride aqui, deletaria a key
        //     do overrides → eff.tags VOLTARIA pro preset (3 tags).
        //   - Solução: setar override = undefined (mascara) em vez de delete.
        //     Resultado: chip some (eff.tags = undefined) e mantém filtro
        //     "limpo" mesmo com preset tendo tags.
        if (prevId === 'brand') {
            const brands = Array.isArray(eff.brands) ? eff.brands : [];
            if (brands.length === 0 && !eff.brand) {
                onOverride('brands', undefined);
                onOverride('brand', undefined);
            }
        } else if (prevId === 'tag') {
            const tags = Array.isArray(eff.tags) ? eff.tags : [];
            if (tags.length === 0 && !eff.tag) {
                onOverride('tags', undefined);
                onOverride('tag', undefined);
            }
        } else if (prevId.startsWith('stock_rule:')) {
            const idx = Number(prevId.split(':')[1]);
            const rules = Array.isArray(eff.stock_rules) ? eff.stock_rules : [];
            const rule = rules[idx];
            if (rule && (rule.value === undefined || rule.value === null)) {
                const nextRules = rules.filter((_, i) => i !== idx);
                if (nextRules.length === 0) onOverride('stock_rules', undefined);
                else onOverride('stock_rules', nextRules);
            }
        }
    }, [openChip, eff.brand, eff.brands, eff.tag, eff.tags, eff.stock_rules, onOverride]);

    const renderDropdownContent = (chipId) => {
        // Stock rule editor — chipId composto `stock_rule:idx`.
        if (chipId.startsWith('stock_rule:')) {
            const idx = Number(chipId.split(':')[1]);
            const rules = Array.isArray(eff.stock_rules) ? eff.stock_rules : [];
            const rule = rules[idx];
            // Providers disponíveis = todos menos os já usados por OUTRAS regras
            // (a do próprio idx pode manter o seu provider atual).
            const usedByOthers = rules.map((r, i) => (i !== idx ? r?.provider : null)).filter(Boolean);
            const available = ['bling', 'ml'].filter((p) => !usedByOthers.includes(p));
            return (
                <StockRuleEditor
                    rule={rule}
                    warehouses={warehouses}
                    availableProviders={available}
                    onChange={(nextRule) => {
                        const nextRules = [...rules];
                        nextRules[idx] = nextRule;
                        onOverride('stock_rules', nextRules);
                    }}
                />
            );
        }
        if (chipId === 'period') {
            return (
                <div style={{ padding: 4, minWidth: 260, maxHeight: 480, overflowY: 'auto' }}>
                    {PERIOD_GROUPS.map((group, gIdx) => (
                        <div
                            key={group.id}
                            style={{
                                borderTop: gIdx > 0 ? `1px solid ${T.line}` : 'none',
                                paddingTop: gIdx > 0 ? 4 : 0,
                                marginTop: gIdx > 0 ? 4 : 0,
                            }}
                        >
                            <div
                                style={{
                                    padding: '6px 10px 2px',
                                    fontSize: 10,
                                    color: T.muted,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.6,
                                }}
                            >
                                {group.label}
                            </div>
                            {group.items.map((opt) => {
                                const rangeText = GetPeriodRangeText(opt.id);
                                return (
                                    <TDItem
                                        key={opt.id}
                                        active={eff.period === opt.id}
                                        onClick={() => {
                                            if (opt.id === 'custom') {
                                                // Fecha o period dropdown e abre o calendário
                                                // num popover dedicado (não inline aqui).
                                                setOpenChip(null);
                                                setDateRangeOpen(true);
                                                return;
                                            }
                                            onOverride('period', opt.id);
                                            onRemoveOverride('dateFrom');
                                            onRemoveOverride('dateTo');
                                            setOpenChip(null);
                                        }}
                                    >
                                        {/* `title` nativo do navegador: hover mostra range absoluto.
                                            Tooltip aplicado no <span> pra ele aparecer no texto inteiro. */}
                                        <span title={rangeText || undefined}>{opt.label}</span>
                                    </TDItem>
                                );
                            })}
                        </div>
                    ))}
                </div>
            );
        }
        if (chipId === 'brand' || chipId === 'tag') {
            const isBrand = chipId === 'brand';
            const opts = isBrand ? brandOpts : tagOpts;
            const selected = isBrand ? effBrands : effTags;
            const overrideKey = isBrand ? 'brands' : 'tags';
            const matchAll = !isBrand && eff.tags_match_all === true;
            return (
                <BrandTagPicker
                    kind={isBrand ? 'brand' : 'tag'}
                    options={opts}
                    selected={selected}
                    matchAll={matchAll}
                    onToggle={(v) => {
                        const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
                        onOverride(overrideKey, next);
                        if (isBrand && eff.brand) onRemoveOverride('brand');
                        if (!isBrand && eff.tag) onRemoveOverride('tag');
                    }}
                    onClear={() => onOverride(overrideKey, [])}
                    onMatchAllChange={!isBrand ? (next) => onOverride('tags_match_all', next) : undefined}
                />
            );
        }
        return null;
    };

    const dropdownStyle = {
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: 0,
        zIndex: 200,
        background: T.bg,
        border: `1px solid ${T.line}`,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        borderRadius: 6,
        overflow: 'hidden',
    };

    return (
        <div
            ref={ref}
            data-faid="Toolbar_Container_Operacoes"
            // Range UTC efetivo do período ativo (YYYY-MM-DD) — exposto pro
            // harness E2E ler EXATAMENTE a janela que o backend aplicou, sem
            // recalcular "N dias atrás" do lado de fora.
            data-period-start={periodRange?.from || ''}
            data-period-end={periodRange?.to || ''}
            // Range ISO UTC por coluna ({col:{from,to}}) — o harness E2E lê isto
            // e valida cada coluna no range EXATO que o frontend computou.
            data-col-ranges={JSON.stringify(colRanges || {})}
            style={{
                minHeight: T.headerH,
                borderBottom: `1px solid ${T.line}`,
                display: 'flex',
                alignItems: 'center',
                padding: '0 20px',
                gap: 12,
                background: T.bg,
                flexShrink: 0,
                flexWrap: 'wrap',
                paddingTop: 8,
                paddingBottom: 8,
            }}
        >
            {/* Backdrop único — cobre toda a viewport quando QUALQUER dropdown
                do Toolbar (chip/+filtro/date-range) está aberto. Click fecha
                tudo. z-index:199, fica abaixo dos dropdowns (z-index 200+). */}
            <Backdrop
                open={Boolean(openChip) || addOpen || dateRangeOpen}
                onClose={() => {
                    setOpenChip(null);
                    setAddOpen(false);
                    setDateRangeOpen(false);
                }}
            />
            <div style={{ position: 'relative', display: 'inline-flex' }}>
                <TInput
                    placeholder="Buscar..."
                    value={search}
                    onChange={onSearchChange}
                    icon={<MagnifyingGlass size={14} color={T.muted} />}
                    width={260}
                    style={{ paddingRight: 56 }}
                    data-faid="Search_Input_Toolbar_Operacoes"
                />
                {/* Lembrete visual: Ctrl+F abre o finder overlay (renderizado
                    em index.jsx). Visualmente igual aos kbd hints do VSCode. */}
                <div
                    style={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                    }}
                >
                    <KbdHint keys="Ctrl+F" compact title="Abrir busca centralizada" />
                </div>
            </div>

            <TDivider vertical style={{ height: 16 }} />

            <PresetSelector
                preset={preset}
                presets={presets}
                onPresetChange={onPresetChange}
                onEditPreset={onEditPreset}
                onNewPreset={onNewPreset}
                onDeletePreset={onDeletePreset}
                editing={editing}
                currentUserId={currentUserId}
            />

            <TDivider vertical style={{ height: 16 }} />

            {/* Em edição, chips/+filtro/LayoutToggle ficam escondidos — o
                PresetBuilder na lateral é a fonte da verdade pra esses controles,
                e o layout é forçado vertical 50/50 enquanto edita. */}
            {!editing &&
                chips.map((chip) => {
                    // ID semântico do chip pra rastreio. Capitaliza o id pra ficar
                    // consistente com a convenção CamelCase (period → Period_Chip…).
                    const chipKey = chip.id.charAt(0).toUpperCase() + chip.id.slice(1);
                    return (
                        <div
                            key={chip.id}
                            style={{ position: 'relative' }}
                            data-faid={`${chipKey}_Chip_Wrapper_Toolbar_Operacoes`}
                        >
                            <TChip
                                active
                                dropdown={chip.dropdown}
                                removable={chip.removable}
                                title={chip.title}
                                style={chip.empty ? { fontStyle: 'italic', opacity: 0.85 } : undefined}
                                data-faid={`${chipKey}_Chip_Toolbar_Operacoes`}
                                onRemove={() => {
                                    // Pra filtros que NÃO vieram de override (vieram do
                                    // preset), `delete overrides[key]` é no-op e o preset
                                    // continua aplicado. Solução: setar override = undefined,
                                    // que MASCARA o preset via spread ({...preset, ...overrides}).
                                    // Trocar de preset reseta overrides (handleSelectPreset),
                                    // então a remoção é só temporária — mesmo comportamento
                                    // pra filtro override ou filtro do preset.
                                    if (chip.id === 'brand') {
                                        onOverride('brands', undefined);
                                        onOverride('brand', undefined);
                                    } else if (chip.id === 'tag') {
                                        onOverride('tags', undefined);
                                        onOverride('tag', undefined);
                                    } else if (chip.kind === 'stock_rule') {
                                        // Stock rules é array — remover UM chip = reconstrói
                                        // o array sem aquela regra; se ficar vazio, mascara
                                        // a key inteira.
                                        const rules = Array.isArray(eff.stock_rules) ? eff.stock_rules : [];
                                        const nextRules = rules.filter((_, i) => i !== chip.ruleIndex);
                                        if (nextRules.length === 0) onOverride('stock_rules', undefined);
                                        else onOverride('stock_rules', nextRules);
                                    } else {
                                        onOverride(chip.id, undefined);
                                    }
                                    if (openChip === chip.id) setOpenChip(null);
                                }}
                                onClick={
                                    chip.dropdown ? () => setOpenChip(openChip === chip.id ? null : chip.id) : undefined
                                }
                            >
                                {chip.label}
                            </TChip>
                            {openChip === chip.id && (
                                <div style={dropdownStyle} data-faid={`${chipKey}_Picker_Dropdown_Toolbar_Operacoes`}>
                                    {renderDropdownContent(chip.id)}
                                </div>
                            )}
                            {/* Popover dedicado pro DateRangePicker — ancorado no chip
                            de período, INDEPENDENTE do dropdown do chip. Click em
                            "Datas específicas..." fecha o dropdown e abre este. */}
                            {chip.id === 'period' && dateRangeOpen && (
                                <div
                                    style={{ ...dropdownStyle, padding: 0 }}
                                    data-faid="Date_Range_Popover_Toolbar_Operacoes"
                                >
                                    <DateRangePicker
                                        from={overrides.dateFrom || periodRange?.from || null}
                                        to={overrides.dateTo || periodRange?.to || null}
                                        onChange={(f, to) => {
                                            onOverride('period', 'custom');
                                            onOverride('dateFrom', f);
                                            onOverride('dateTo', to);
                                            setDateRangeOpen(false);
                                        }}
                                        onClear={() => {
                                            onRemoveOverride('dateFrom');
                                            onRemoveOverride('dateTo');
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}

            {!editing && (
                <div ref={addRef} style={{ position: 'relative' }} data-faid="Add_Filter_Wrapper_Toolbar_Operacoes">
                    <TChip
                        muted
                        dropdown
                        onClick={() => setAddOpen((o) => !o)}
                        data-faid="Add_Filter_Chip_Toolbar_Operacoes"
                    >
                        + filtro
                    </TChip>

                    {addOpen && (
                        <div style={{ ...dropdownStyle, width: 220 }} data-faid="Add_Filter_Dropdown_Toolbar_Operacoes">
                            <div
                                style={{
                                    padding: '8px 14px 6px',
                                    fontSize: 10,
                                    color: T.muted,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.6,
                                    borderBottom: `1px solid ${T.line}`,
                                }}
                            >
                                Adicionar filtro
                            </div>
                            {!eff.period && (
                                <TDItem
                                    onClick={() => {
                                        onOverride('period', 'last_7d');
                                        setAddOpen(false);
                                        setOpenChip('period');
                                    }}
                                >
                                    Período
                                </TDItem>
                            )}
                            {!Array.isArray(eff.brands) && !eff.brand && (
                                <TDItem
                                    onClick={() => {
                                        // Inicializa o override + abre direto o picker do
                                        // chip (que agora SEMPRE renderiza quando override
                                        // existe, mesmo array vazio). Sem pendingFilter
                                        // intermediário → impossível ter 2 pickers visíveis.
                                        onOverride('brands', []);
                                        setAddOpen(false);
                                        setOpenChip('brand');
                                    }}
                                >
                                    Marca
                                </TDItem>
                            )}
                            {!Array.isArray(eff.tags) && !eff.tag && (
                                <TDItem
                                    onClick={() => {
                                        onOverride('tags', []);
                                        setAddOpen(false);
                                        setOpenChip('tag');
                                    }}
                                >
                                    Tag
                                </TDItem>
                            )}
                            {/* Regra de estoque por provider — só aparece se NÃO existir
                            uma regra do mesmo provider. Combinação válida: 1 Bling +
                            1 ML simultâneos. Evita confusão de "ML > 10 e ML > 20". */}
                            {!HasStockRuleFor('bling', eff.stock_rules) && (
                                <TDItem
                                    onClick={() => {
                                        const current = Array.isArray(eff.stock_rules) ? eff.stock_rules : [];
                                        const nextRules = [...current, MakeDefaultStockRule('bling', warehouses)];
                                        onOverride('stock_rules', nextRules);
                                        const newIdx = nextRules.length - 1;
                                        setAddOpen(false);
                                        setOpenChip(`stock_rule:${newIdx}`);
                                    }}
                                >
                                    Estoque Bling
                                </TDItem>
                            )}
                            {!HasStockRuleFor('ml', eff.stock_rules) && (
                                <TDItem
                                    onClick={() => {
                                        const current = Array.isArray(eff.stock_rules) ? eff.stock_rules : [];
                                        const nextRules = [...current, MakeDefaultStockRule('ml', warehouses)];
                                        onOverride('stock_rules', nextRules);
                                        const newIdx = nextRules.length - 1;
                                        setAddOpen(false);
                                        setOpenChip(`stock_rule:${newIdx}`);
                                    }}
                                >
                                    Estoque Mercado Livre
                                </TDItem>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div style={{ flex: 1 }} />

            {/* Ordem das ações: Refresh · Carrinho · Depósitos · Exportar · Exibição.
                Carrinho só aparece pra quem tem permissão de criar OC. Refresh
                força um reload completo do snapshot (sellout+products+stock) — útil
                após a aba ficar muito tempo aberta. */}
            <TBtn
                variant="default"
                size="sm"
                onClick={onReload}
                disabled={reloading}
                title="Atualizar dados (recarrega vendas, catálogo e estoque)"
                data-faid="Refresh_Button_Toolbar_Operacoes"
            >
                <ArrowClockwise
                    size={14}
                    style={reloading ? { animation: 'opSpin 0.8s linear infinite' } : undefined}
                />
                Atualizar
            </TBtn>

            {showCart && <CartButton count={cartCount} onClick={onOpenCart} />}

            <TBtn
                variant="default"
                size="sm"
                disabled={!presetIncludesBlingColumn(preset)}
                onClick={() => onOpenWarehouseConfig?.()}
                title={
                    presetIncludesBlingColumn(preset)
                        ? 'Configurar quais depósitos Bling somam na coluna de estoque'
                        : 'Disponível quando o preset exibe a coluna Bling'
                }
                data-faid="Warehouse_Config_Button_Toolbar_Operacoes"
            >
                <Warehouse size={14} /> Depósitos
            </TBtn>

            <TBtn
                variant="default"
                size="sm"
                onClick={onExport}
                title="Exportar resultado em CSV"
                data-faid="Export_Button_Toolbar_Operacoes"
                // #107C41 = verde oficial do Excel. Texto branco pra contraste.
                // Borda da mesma cor pra não ficar pálido em temas claros.
                style={{ background: '#107C41', color: '#fff', border: '1px solid #107C41' }}
            >
                <FileExport size={14} /> Exportar
            </TBtn>

            {!editing && <LayoutToggle mode={layoutMode} onChange={onLayoutChange} />}
        </div>
    );
}
